import { SSLogoIcon as SSLogo } from "../components/SSLogo";
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { LogIn, Zap, Mail, Lock, Globe } from 'lucide-react';

export default function LoginPage() {
  const { t, isRTL, toggleLanguage, lang } = useLanguage();
  const { login } = useAuth();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error(isRTL ? 'الرجاء ملء جميع الحقول' : 'Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success(isRTL ? 'تم تسجيل الدخول بنجاح' : 'Logged in successfully');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background noise-bg flex">
      {/* Left / Right side illustration */}
      <div className={`hidden lg:flex lg:w-1/2 hero-gradient items-center justify-center p-12 ${isRTL ? 'order-2' : 'order-1'}`}>
        <div className="max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
              <SSLogo size={36} />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold">SHOP & SHIP</h1>
              <p className="text-sm text-muted-foreground">{isRTL ? 'منصة التجارة' : 'Sell & Earn'}</p>
            </div>
          </div>
          <h2 className="font-heading text-4xl font-bold mb-4">
            {isRTL ? 'اكسب عمولات بإكمال مهام بسيطة' : 'Earn commissions by completing simple tasks'}
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            {isRTL 
              ? 'انضم إلى آلاف المستخدمين الذين يكسبون من متاجر متعددة بنظام عمولات متدرج'
              : 'Join thousands of users earning from multiple marketplaces with a tiered commission system'
            }
          </p>
        </div>
      </div>

      {/* Form side */}
      <div className={`flex-1 flex items-center justify-center p-6 ${isRTL ? 'order-1' : 'order-2'}`}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Language toggle */}
          <div className="flex justify-end mb-6">
            <Button variant="ghost" size="sm" onClick={toggleLanguage} className="gap-2">
              <Globe className="w-4 h-4" />
              {lang === 'ar' ? 'English' : 'عربي'}
            </Button>
          </div>

          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
              <SSLogo size={32} />
            </div>
            <h1 className="font-heading text-xl font-bold">SHOP & SHIP</h1>
          </div>

          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-2xl font-heading">{t('login')}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">{t('email')}</label>
                  <div className="relative">
                    <Mail className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('emailPlaceholder')}
                      className={isRTL ? 'pr-10' : 'pl-10'}
                      dir="ltr"
                      data-testid="auth-email-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">{t('password')}</label>
                  <div className="relative">
                    <Lock className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground ${isRTL ? 'right-3' : 'left-3'}`} />
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('passwordPlaceholder')}
                      className={isRTL ? 'pr-10' : 'pl-10'}
                      data-testid="auth-password-input"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full gap-2" 
                  disabled={loading}
                  data-testid="auth-submit-button"
                >
                  <LogIn className="w-4 h-4" />
                  {loading ? '...' : t('login')}
                </Button>
              </form>

              {/* Divider */}
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground">{isRTL ? 'أو' : 'OR'}</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Google Login - REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
              <Button variant="outline" className="w-full gap-3 h-11" onClick={() => {
                const redirectUrl = window.location.origin + '/dashboard';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }} data-testid="google-login-btn">
                <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                {isRTL ? 'تسجيل الدخول بـ Google' : 'Sign in with Google'}
              </Button>

              <p className="text-sm text-center text-muted-foreground mt-4">
                {t('dontHaveAccount')}{' '}
                <Link to="/register" className="text-primary hover:underline font-medium">
                  {t('register')}
                </Link>
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
